import { Guid } from "guid-typescript";
import { AccessLevelType, AccessLevelTypeDescription, DocumentLegalValueCombination } from "./edocs.model";
import { Enums } from "../shared/enums";

export class ConfigSchemaFlowDefinition {
    hasAutomaticFlowDefinitionDeactivation? = false;
    daysSinceFlowDefinitionActivationToAutoDeactivate?: number;
    dateTimeToAutoDeactivateFlowDefinition?: Date;

    hasAutomaticFlowDefinitionActivation?: boolean = false;
    daysSinceFlowDefinitionPublicationToAutoActivate?: number;
    dateTimeToAutoActivateFlowDefinition?: Date;

    hasAutomaticFlowInstanceCancellation? = false;
    daysSinceFlowInstanceStartToAutoCancel?: number;
    daysSinceFlowInstanceLastUpdateToAutoCancel?: number;
    dateTimeToAutoCancelFlowInstance?: Date;

    shouldAddInfoToEDocsTitles? = false;
    shouldNotAddInfoToDocuments? = false;
    addUserNameToEDocsTitles? = false;
    addUserCpfToEDocsTitles? = false;
    formFieldNameAddedToEDocsTitles?: string;

    shouldAddInfoToEDocsTitles_Additional? = false;
    addUserNameToEDocsTitles_Additional? = false;
    addUserCpfToEDocsTitles_Additional? = false;
    formFieldNameAddedToEDocsTitles_Additional?: string;

    shouldIgnoreTargetBasedRoleFiltering? = false;

    hasCancellationByDeliberation? = false;
    cancellationDocumentClass?: ConfigSchemaSectionDocumentClass = new ConfigSchemaSectionDocumentClass();
    cancellationAccessLevel?: ConfigSchemaSectionAccessLevel = new ConfigSchemaSectionAccessLevel();

    whitelist?: ConfigSchemaSectionEDocsLocation[] = [];
}

export class ConfigSchema {
    taskForward? = new ConfigSchemaTaskForward();
    taskForm? = new ConfigSchemaTaskForm();
    taskStartInboundApi? = new ConfigSchemaTaskStartInboundApi();
    taskApprove? = new ConfigSchemaTaskApprove();
    taskAcknowledge? = new ConfigSchemaTaskAcknowledge();
    taskRegisterProcess? = new ConfigSchemaTaskRegisterProcess();
    taskDispatchProcess? = new ConfigSchemaTaskDispatchProcess();
    taskAddToProcess? = new ConfigSchemaTaskAddToProcess();
    taskOutboundApi? = new ConfigSchemaTaskOutboundApi();
    taskGatewayRules? = new ConfigSchemaTaskGatewayRules();
    taskGatewayPaths? = new ConfigSchemaTaskGatewayPaths();
    taskFinishContent? = new ConfigSchemaTaskFinishContent();
}

export class ConfigSchemaSectionAccessLevel {
    id?: AccessLevelType = AccessLevelType.Organizational;
    name?: string = AccessLevelTypeDescription.get(AccessLevelType.Organizational);
    legalReasoningsIds?: string[] = [];
    legalReasoningsNames?: string[] = [];
}

export class ConfigSchemaSectionDocumentClass {
    id?: string = null;
    name?: string = null;
}

export class ConfigSchemaSectionEDocsLocation {
    id?: string = null;
    name?: string = null;
    type?: LocationType = null;
    flowInbox?: string = null;
    patriarchId?: string = null;
    patriarchName?: string = null;
}

export class ConfigSchemaTaskForward {
    toImmediateSupervisor? = false;
    toOwnerImmediateSupervisor? = false;
    toOwnerHeadOfOrganization? = false;
    toOwnRole? = false;
    conditionalRecipients?: ConditionalRecipient[] = [];
    recipient?: ConfigSchemaSectionEDocsLocation = new ConfigSchemaSectionEDocsLocation();
    accessLevel?: ConfigSchemaSectionAccessLevel = new ConfigSchemaSectionAccessLevel();
}

export class ConfigSchemaTaskForm {
    documentClass?: ConfigSchemaSectionDocumentClass = new ConfigSchemaSectionDocumentClass();
    accessLevel?: ConfigSchemaSectionAccessLevel = new ConfigSchemaSectionAccessLevel();
}

export class ConfigSchemaTaskStartInboundApi {
    fileUrls?: any;
}

export class ConfigSchemaTaskApprove extends ConfigSchemaTaskForm {
    extraContent?: ConfigSchemaSectionExtraContent = new ConfigSchemaSectionExtraContent();
}

export class ConfigSchemaTaskAcknowledge extends ConfigSchemaTaskForm {
    extraContent?: ConfigSchemaSectionExtraContent = new ConfigSchemaSectionExtraContent();
}

export class ConfigSchemaTaskRegisterProcess {
    processLocationId?: string = null;
    processLocationName? = '';
    processSummary? = '';
    stakeholders?: Stakeholder[] = [];
    processClass?: ConfigSchemaSectionDocumentClass = new ConfigSchemaSectionDocumentClass();
}

export class ConfigSchemaTaskDispatchProcess {
    dispatchSummary? = '';
    toPublicAgent? = false;
    toOwnerImmediateSupervisor? = false;
    toOwnerHeadOfOrganization? = false;
    conditionalRecipients?: ConditionalRecipient[] = [];
    accessLevel?: ConfigSchemaSectionAccessLevel = new ConfigSchemaSectionAccessLevel();
    dispatchRecipient?: ConfigSchemaSectionEDocsLocation = new ConfigSchemaSectionEDocsLocation();
}

export class ConfigSchemaTaskAddToProcess {
    addToProcessSummary? = '';
    processOrigin: Enums.ProcessOrigin = Enums.ProcessOrigin.Instance;
    processProtocolFormField?: string;
    processProtocol?: string;
    accessLevel?: ConfigSchemaSectionAccessLevel = new ConfigSchemaSectionAccessLevel();
    cancellationDocumentClass?: ConfigSchemaSectionDocumentClass = new ConfigSchemaSectionDocumentClass();
}

export class ConfigSchemaTaskOutboundApi {
    publicSystemName? = '';
    httpMethod?: HttpMethod = HttpMethod.Get;
    url?: string = null;
    urlParameters?: any = {};
    ignoreErrors?: boolean;
    backgroundExecution?: boolean;
    errorMessage?: string = null;
    authentication?: AuthenticationModel = new AuthenticationModel();
    resolver?: ApiResolver = new ApiResolver();
    data?: any = {};
}

export class ConfigSchemaTaskGatewayRules {
    httpSuccessRule?: boolean = null;
    message? = '';
    propertyKey?: string = null;
    ruleOperator?: GatewayRuleOperator = null;
    propertyValue?: any = null;
    shouldIgnoreNoValueError? = false;
    shouldIgnoreNonNumberError? = false;
}

export class ConfigSchemaTaskGatewayPaths {
    firstGatewayPathName? = 'Sequência A';
    secondGatewayPathName? = 'Sequência B';
    isDivisionByDeliberation? = false;
    logicalOperator?: ('and' | 'or') = 'and';
    conditions?: GatewayPathsCondition[] = [];
}

export class ConditionalRecipient {
    formFieldId?: string = null;
    value?: string = null;
    isOperationEqual?: boolean = true;
    timestamp?: string = new Date().toISOString();
    recipient?: ConfigSchemaSectionEDocsLocation = new ConfigSchemaSectionEDocsLocation();

    public constructor(init?: Partial<ConditionalRecipient>) {
        Object.assign(this, init);
    }
}

export class GatewayPathsCondition {
    formField?: string;
    ruleOperator?: GatewayRuleOperator = GatewayRuleOperator.Equal;
    referenceValue?: any;
    referenceValueDisabled? = false;
    timestamp?: string = new Date().toISOString();
}

export class ConfigSchemaTaskFinishContent {
    extraContent?: ConfigSchemaSectionExtraContent = new ConfigSchemaSectionExtraContent();
}

export class ConfigSchemaSectionExtraContent {
    contentType?: ExtraContentType = null;
    message? = '';
    waitingMessage? = '';
    documentClass?: ConfigSchemaSectionDocumentClass = new ConfigSchemaSectionDocumentClass();
    accessLevel?: ConfigSchemaSectionAccessLevel = new ConfigSchemaSectionAccessLevel();
    attachments?: ExtraContentAttachment[] = [];
}

export class ExtraContentAttachment {
    id?: string = Guid.create().toString();
    label? = '';
    documentClass?: ConfigSchemaSectionDocumentClass = new ConfigSchemaSectionDocumentClass();
    accessLevel?: ConfigSchemaSectionAccessLevel = new ConfigSchemaSectionAccessLevel();
    documentLegalValueCombination?: DocumentLegalValueCombination = null;
    timestamp?: string = new Date().toISOString();
}

export enum LocationType {
    Organization = 0,
    Group = 1,
    Comission = 2,
    Unit = 3
}

export class Stakeholder {
    id?: string = null;
    name?: string = null;
    citizenCpfFormField?: string = null;
    isCitizenFlowInstanceOwner? = false;
    companyName?: string = null;
    companyNameFormField?: string = null;
    cnpj?: string = null;
    cnpjFormField?: string = null;
    email?: string = null;
    emailFormField?: string = null;
    type: StakeholderType = null;
    timestamp?: string = new Date().toISOString();
    isMandatory?: boolean = true;

    public constructor(init?: Partial<Stakeholder>) {
        Object.assign(this, init);
    }
}

export enum StakeholderType {
    Patriarch = 1,
    Organization = 2,
    Unit = 3,
    Group = 4,
    Comission = 5,
    PublicAgent = 6,
    Citizen = 7,
    Company = 8
}

export const StakeholderTypeDescription = new Map<number, string>([
    [StakeholderType.Patriarch, 'Patriarca'],
    [StakeholderType.Organization, 'Órgão'],
    [StakeholderType.Unit, 'Setor'],
    [StakeholderType.Group, 'Grupo'],
    [StakeholderType.Comission, 'Comissão'],
    [StakeholderType.PublicAgent, 'Servidor'],
    [StakeholderType.Citizen, 'Cidadão'],
    [StakeholderType.Company, 'Pessoa Jurídica']
]);

export enum ExtraContentType {
    Text = 1,
    Attachment = 2
}

// #region [Task API]
export enum AuthenticationSchemeType {
    None = 0,
    Basic = 3,
    ApiKey = 4,
    Jwt = 5
}

export const AuthenticationSchemeTypeDescription = new Map<number, string>([
    [AuthenticationSchemeType.None, 'Sem Autenticação'],
    [AuthenticationSchemeType.Basic, 'Básica'],
    [AuthenticationSchemeType.ApiKey, 'API Key'],
    [AuthenticationSchemeType.Jwt, 'JWT']
]);

export enum HttpMethod {
    Get = 0,
    Post = 1,
    Put = 2,
    Delete = 3
}

export const HttpMethodDescription = new Map<number, string>([
    [HttpMethod.Get, 'GET'],
    [HttpMethod.Post, 'POST'],
    [HttpMethod.Put, 'PUT'],
    [HttpMethod.Delete, 'DELETE']
]);

export class AuthenticationModel {
    authenticationScheme?: AuthenticationSchemeType = AuthenticationSchemeType.None;
    basicOrApiKey?: string = null;
    clientId?: string = null;
    clientSecret?: string = null;
    scopes?: string = null;
    tokenEndpoint?: string = null;
}

export class ApiResolver {
    id?: string = null;
    name?: string = null;
    type?: LocationType = null;
}

export enum GatewayRuleOperator {
    Equal = 0,
    NotEqual = 1,
    GreaterThan = 2,
    GreaterOrEqual = 3,
    LessThan = 4,
    LessOrEqual = 5,
    IsTrue = 6,
    IsFalse = 7,
    IsNull = 8,
    IsNotNull = 9,
    IsEmpty = 10,
    IsNotEmpty = 11,
    Contains = 12
}

export const GatewayRuleOperatorDescription = new Map<number, string>([
    [GatewayRuleOperator.Equal, 'é igual a'],
    [GatewayRuleOperator.NotEqual, 'é diferente de'],
    [GatewayRuleOperator.GreaterThan, 'é maior que'],
    [GatewayRuleOperator.GreaterOrEqual, 'é maior que ou igual a'],
    [GatewayRuleOperator.LessThan, 'é menor que'],
    [GatewayRuleOperator.LessOrEqual, 'é menor que ou igual a'],
    [GatewayRuleOperator.IsTrue, 'é verdadeiro'],
    [GatewayRuleOperator.IsFalse, 'é falso'],
    [GatewayRuleOperator.IsNull, 'é nulo'],
    [GatewayRuleOperator.IsNotNull, 'não é nulo'],
    [GatewayRuleOperator.IsEmpty, 'é vazio'],
    [GatewayRuleOperator.IsNotEmpty, 'não é vazio'],
    [GatewayRuleOperator.Contains, 'contém']
]);
// #endregion
